import React, { useCallback, useEffect, useState } from "react";
import {
	MDBContainer,
	MDBRow,
	MDBFooter,
	MDBCol,
	MDBCard,
	MDBCardBody,
} from "mdbreact";
import logo from "../../images/logo.png";
import { message, Button, Form, Typography, Input, Progress } from "antd";
import { useNavigate } from "react-router-dom";
import Firebase from "../Firebase/Firebase";
import { getAuth, signInWithCustomToken } from "firebase/auth";
import { getFunctionsApiBaseUrl } from "../utils/functionsApi";

const auth = getAuth(Firebase);
const { Text } = Typography;

const evaluatePasswordStrength = (value) => {
	if (!value) {
		return {
			label: "",
			percent: 0,
			status: "normal",
			type: "secondary",
		};
	}

	let score = 0;
	if (value.length >= 8) score++;
	if (/[A-Z]/.test(value)) score++;
	if (/[a-z]/.test(value)) score++;
	if (/\d/.test(value)) score++;
	if (/[^A-Za-z0-9]/.test(value)) score++;

	if (score <= 2) {
		return {
			label: "Weak",
			percent: 30,
			status: "exception",
			type: "danger",
		};
	}

	if (score <= 3) {
		return {
			label: "Medium",
			percent: 60,
			status: "normal",
			type: "warning",
		};
	}

	return {
		label: "Strong",
		percent: 100,
		status: "success",
		type: "success",
	};
};

const Login = () => {
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const [loading, setLoading] = useState(false);
	const [passwordStrength, setPasswordStrength] = useState({
		label: "",
		percent: 0,
		status: "normal",
		type: "secondary",
	});
	const [capsLockOn, setCapsLockOn] = useState(false);

	const resetLoginUiState = useCallback(() => {
		setPasswordStrength({
			label: "",
			percent: 0,
			status: "normal",
			type: "secondary",
		});
		setCapsLockOn(false);
	}, []);

	const clearLoginFields = useCallback(() => {
		form.setFieldsValue({
			identifier: "",
			password: "",
		});
		resetLoginUiState();
	}, [form, resetLoginUiState]);

	useEffect(() => {
		// Keep transient UI clean without wiping browser autofill suggestions.
		resetLoginUiState();
	}, [resetLoginUiState]);

	const handlePasswordKeyEvent = (e) => {
		if (typeof e.getModifierState === "function") {
			const isOn = e.getModifierState("CapsLock");
			setCapsLockOn(isOn);
		}
	};

	const handleValuesChange = (changedValues) => {
		if (Object.prototype.hasOwnProperty.call(changedValues, "password")) {
			const nextVal = changedValues.password || "";
			setPasswordStrength(evaluatePasswordStrength(nextVal));
		}
	};

	const passwordExtra = passwordStrength.label || capsLockOn ? (
		<div className="mt-1">
			{passwordStrength.label ? (
				<div className="mb-1">
					<Progress
						percent={passwordStrength.percent}
						size="small"
						showInfo={false}
						status={passwordStrength.status}
					/>
					<Text type={passwordStrength.type} className="text-xs">
						{passwordStrength.label} password
					</Text>
				</div>
			) : null}

			{capsLockOn && (
				<Text type="warning" className="text-xs">
					Caps Lock is ON - you may enter your password incorrectly.
				</Text>
			)}
		</div>
	) : null;

	const loginFunction = async (values) => {
		let isSuccessful = false;
		const identifier = values.identifier ? values.identifier.trim() : "";
		const password = values.password;

		if (!identifier) {
			message.error("Please enter your email or phone number!");
			return;
		}
		if (!password) {
			message.error("Please enter your password!");
			return;
		}

		setLoading(true);

		try {
			const apiBaseUrl = getFunctionsApiBaseUrl();
			const response = await fetch(`${apiBaseUrl}/login`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify({
					identifier,
					password,
				}),
			});
			const contentType = response.headers.get("content-type") || "";
			const data = contentType.includes("application/json")
				? await response.json()
				: null;

			if (!response.ok) {
				const errMsg =
					data?.error ||
					data?.message ||
					"Unable to login with provided credentials. Please check your details.";
				message.error(errMsg);
				return;
			}

			if (!data || typeof data !== "object") {
				message.error("Login failed: invalid response from server.");
				return;
			}

			const { customToken, role, permissions } = data || {};

			if (!customToken) {
				message.error("Login failed: missing token from server.");
				return;
			}
			await signInWithCustomToken(auth, customToken);
			await auth.currentUser?.getIdToken(true);
			if (role) {
				localStorage.setItem("talmidsRole", role);
			}
			if (permissions) {
				localStorage.setItem("talmidsPermissions", JSON.stringify(permissions));
			}

			message.success("Login successful.");
			isSuccessful = true;
			navigate("/home");
		} catch (error) {
			console.error("Login error:", error);
			message.error(
				"Network or server error while logging in. Please try again."
			);
		} finally {
			if (!isSuccessful) {
				clearLoginFields();
			}
			setLoading(false);
		}
	};

	return (
		<div className="vh-100 d-flex justify-content-center align-items-center">
			<MDBContainer>
				<MDBRow center={true}>
					<MDBCol md="4" lg="5" className="my-5">
						<MDBCard className="p-[4rem]">
							<MDBCardBody>
								<div className="text-center mb-4">
									<div className="mb-3">
										<img
											src={logo}
											alt="logo"
											className="rounded mx-auto d-block bg-primary p-1"
											style={{ maxHeight: 80 }}
										/>
									</div>
									<Typography.Title level={4} style={{ marginBottom: 0 }}>
										Welcome back
									</Typography.Title>
									<Text type="secondary">Sign in to continue</Text>
								</div>

								<Form
									form={form}
									layout="vertical"
									autoComplete="on"
									onFinish={loginFunction}
									onValuesChange={handleValuesChange}
								>
									<Form.Item
										label="Email or Phone"
										name="identifier"
										rules={[
											{
												required: true,
												message: "Please enter your email or phone number!",
											},
										]}
									>
										<Input
											size="large"
											type="text"
											autoComplete="username"
											placeholder="Enter email or phone number"
										/>
									</Form.Item>

									<Form.Item
										label="Password"
										name="password"
										extra={passwordExtra}
										rules={[
											{ required: true, message: "Please enter your password!" },
										]}
									>
										<Input.Password
											size="large"
											autoComplete="current-password"
											placeholder="Enter your password"
											onKeyDown={handlePasswordKeyEvent}
											onKeyUp={handlePasswordKeyEvent}
										/>
									</Form.Item>

									<div className="text-center mt-3">
										<Button
											className="w-50"
											htmlType="submit"
											type="primary"
											shape="default"
											size="large"
											loading={loading}
										>
											LOGIN
										</Button>
									</div>
								</Form>
							</MDBCardBody>

							<MDBFooter>
								<div className="text-center text-black-50 d-flex justify-content-center mt-2 mb-3">
									<Button
										type="link"
										className="text-primary text-[12px] p-0"
										onClick={() => navigate("/forgot")}
									>
										Forgot your password?
									</Button>
								</div>
							</MDBFooter>
						</MDBCard>
					</MDBCol>
				</MDBRow>
			</MDBContainer>
		</div>
	);
};

export default Login;
